/*
 * @Copyright 2020. Institute for Future Intelligence, Inc.
 * @author Charles Xie
 */

import * as firebase from 'firebase/app';
import 'firebase/firebase-database';
import 'firebase/firebase-auth';
import 'firebase/firebase-firestore'
import {examples, user, emailClient, loadInitialState} from "./Main";
import {State} from "./State";
import $ from "jquery";
import {Util} from "./Util";
import {StateIO} from "./StateIO";
import {CloudFileInfo} from "./CloudFileInfo";
import {Software} from "./Constants";

export class Firebase {

  reference;
  firestore;

  private fileName: string = 'My File';
  private selectedIndex: number = -1;

  constructor() {
    let config = {
      apiKey: process.env.FIREBASE_API_KEY,
      authDomain: process.env.AUTH_DOMAIN,
      projectId: process.env.PROJECT_ID,
      storageBucket: process.env.STORAGE_BUCKET,
      databaseURL: process.env.DATABASE_URL,
      messagingSenderId: process.env.MESSAGING_SENDER_ID,
      appId: process.env.APP_ID
    };
    firebase.initializeApp(config);
    this.reference = firebase.database();
    this.firestore = firebase.firestore();
    this.who();
  }

  private who(): void {
    // do not use firebase.auth().currentUser - currentUser might be null because the auth object has not finished initializing.
    // If you use an observer to keep track of the user's sign-in status, you don't need to handle this case.
    firebase.auth().onAuthStateChanged(u => {
      if (u) {
        user.displayName = u.displayName;
        user.email = u.email;
        user.photoURL = u.photoURL;
        user.uid = u.uid;
        const signinLabel = document.getElementById("main-page-sign-in-label");
        signinLabel.innerHTML = (user.photoURL ? '<img height="24" src="' + user.photoURL + '"/> ' : '') + user.displayName;
        signinLabel.title = 'Click to view my files';
        document.getElementById("main-page-signout-button").style.display = 'inline';
      }
      loadInitialState(); // load the initial state after we recognize the user
    });
  }

  addFile(title: string): void {
    if (user.email) {
      let doc = this.firestore.collection("users").doc(user.email);
      if (doc) {
        doc.collection("files").doc(title).set(JSON.parse(JSON.stringify(new State(Date.now(), user.email))));
      }
    }
  }

  clickSignIn(): void {
    if (user.email) {
      this.showFiles();
    } else {
      this.signin();
    }
  }

  private signin(): void {
    const provider = new firebase.auth.GoogleAuthProvider();
    firebase.auth().signInWithPopup(provider).then(result => {
      user.displayName = result.user.displayName;
      user.email = result.user.email;
      user.photoURL = result.user.photoURL;
      user.uid = result.user.uid;
      this.registerUser(user.email);
      const signinLabel = document.getElementById("main-page-sign-in-label");
      signinLabel.innerHTML = (user.photoURL ? '<img height="24" src="' + user.photoURL + '"/> ' : '') + user.displayName;
      signinLabel.title = 'Click to view my files';
      document.getElementById("main-page-signout-button").style.display = 'inline';
    }).catch(error => {
      console.log("Error: ", error);
    });
  }

  private async registerUser(email: string): Promise<any> {
    const found = await this.firestore.collection("users").get().then(querySnapshot => {
      for (let doc of querySnapshot.docs) {
        if (doc.id === email) {
          return true;
        }
      }
      return false;
    });
    if (!found) {
      this.firestore.collection("users").doc(user.email).set({
        email: user.email,
        uid: user.uid,
        photoURL: user.photoURL,
        displayName: user.displayName
      }).then(docRef => {
        if (docRef) console.log("Document written with ID: ", docRef.id);
      }).catch(error => {
        console.error("Error adding document: ", error);
      });
    }
  }

  signout(): void {
    firebase.auth().signOut().then(() => {
      const signinLabel = document.getElementById("main-page-sign-in-label");
      signinLabel.innerHTML = 'Sign in';
      signinLabel.title = 'Click to sign in';
      document.getElementById("main-page-signout-button").style.display = 'none';
      user.nullify();
      this.setCloudFileTitle('');
    }).catch(error => {
      console.log("Error: ", error);
    });
  }

  saveFileInCloud(): void {
    let d = $("#modal-dialog");
    if (user.email) {
      const inputFieldId: string = "save-file-name-field";
      d.html(`<div style="font-family: Arial; line-height: 30px; font-size: 90%;">
        Title:<br><input type="text" id="${inputFieldId}" style="width: 260px;" value="${this.fileName}"></div>`);
      let inputElement = document.getElementById(inputFieldId) as HTMLInputElement;
      Util.selectField(inputElement, 0, inputElement.value.indexOf("."));
      let okFunction = () => {
        d.dialog('close');
        this.fileName = inputElement.value;
        this.addFile(this.fileName);
        this.setCloudFileTitle(this.fileName);
      };
      inputElement.addEventListener("keyup", (e) => {
        if (e.key == "Enter") {
          okFunction();
        }
      });
      d.dialog({
        resizable: false,
        modal: true,
        title: "Save as",
        height: 200,
        width: 300,
        position: {
          my: 'center center',
          at: 'center center',
          of: window
        },
        buttons: {
          'OK': okFunction,
          'Cancel': () => d.dialog('close')
        }
      });
    } else {
      d.html("<div style='font-size: 90%;'>Please sign in to save file in cloud." +
        "<br>If you cannot sign in, you can save<br>your file to the disk.</div>").dialog({
        resizable: false,
        modal: true,
        title: "Information",
        height: 200,
        width: 300,
        buttons: {
          'OK': () => d.dialog('close')
        }
      });
    }
  }

  openFile(userId: string, fileName: string): void {
    this.firestore.collection("users").doc(userId).collection("files").doc(fileName).get().then(doc => {
      if (doc.data()) {
        StateIO.load(doc.data());
      } else {
        let aboveDialog = $("#modal-dialog-above").html("<div style='font-size: 90%;'>The shared file <b>"
          + this.fileName + "</b> is not found. It may have been deleted by the owner <b>" + userId + "</b>.</div>");
        aboveDialog.dialog({
          resizable: false,
          modal: true,
          title: "Shared File Not Found",
          height: 200,
          width: 350,
          buttons: {
            'OK': () => aboveDialog.dialog('close')
          }
        });
      }
    });
    this.setCloudFileTitle(fileName + (userId === user.email ? "" : " (from " + userId.substr(0, Math.min(8, userId.length)) + "...)"));
    examples.deselect();
  }

  private async showFiles() {
    // reset
    this.selectedIndex = -1;

    // fetch owner's file information from the cloud
    let cloudFiles: CloudFileInfo[] = await this.firestore.collection("users").doc(user.email).collection("files").get().then(querySnapshot => {
      let a: CloudFileInfo[] = [];
      querySnapshot.forEach(doc => {
        a.push(new CloudFileInfo(doc.data().timestamp, doc.id, doc.data().owner));
      });
      return a;
    }).catch(error => {
      console.log("Error getting files:", error);
    });

    // fetch shared files from the cloud
    const sharedFiles: CloudFileInfo[] = await this.firestore.collection("users").doc(user.email).collection("shared").get().then(querySnapshot => {
      let a: CloudFileInfo[] = [];
      querySnapshot.forEach(doc => {
        let owner = doc.id;
        let files: string[] = doc.data()["files"];
        if (files) {
          for (let x of files) {
            a.push(new CloudFileInfo(undefined, x, owner));
          }
        }
      });
      return a;
    }).catch(error => {
      console.log("Error getting files:", error);
    });
    if (sharedFiles.length > 0) {
      cloudFiles = cloudFiles.concat(sharedFiles);
      cloudFiles.sort((a, b) => {
        return a.fileName.localeCompare(b.fileName);
      });
    }

    // put results in a selectable table
    let s: string = "<table class='selectableTable' width='100%' id='cloud-files-table'><tr><th>Name</th><th>Owner</th><th>Date</th></tr>";
    for (let file of cloudFiles) {
      s += "<tr><td>" + file.fileName + "</td>";
      if (file.owner) {
        s += "<td title='" + file.owner + "'>" + (file.owner === user.email ? "Me" : file.owner.substr(0, Math.min(8, file.owner.length)) + "...") + "</td>";
      } else {
        s += "<td>---</td>";
      }
      s += "<td>" + (file.timestamp ? new Date(file.timestamp).toLocaleString() : "---") + "</td></tr>";
    }
    s += "</table>";
    let d = $("#modal-dialog");
    d.html("<div>" + s + "</div>");
    let table = document.getElementById('cloud-files-table') as HTMLTableElement;
    let selected = table.getElementsByClassName('selected') as HTMLCollection;
    table.onclick = (e) => {
      if (selected[0]) selected[0].className = ''; // deselect the selected row if any
      let cellElement = e.target as HTMLTableCellElement; // target is a cell element
      let rowElement = cellElement.parentElement as HTMLTableRowElement; // the parent of a cell element is a row element
      this.selectedIndex = rowElement.rowIndex;
      if (this.selectedIndex > 0) {
        cellElement.parentElement.className = 'selected';
      }
    };

    // open function
    let open = () => {
      if (this.selectedIndex > 0) {
        this.fileName = table.rows[this.selectedIndex].cells[0].innerHTML;
        const owner = table.rows[this.selectedIndex].cells[1].title;
        this.openFile(owner ? owner : user.email, this.fileName);
        d.dialog('close');
      } else {
        this.pleaseSelectFile();
      }
    };

    // delete function
    let deletion = () => {
      if (this.selectedIndex > 0) {
        this.fileName = table.rows[this.selectedIndex].cells[0].innerHTML;
        const owner = table.rows[this.selectedIndex].cells[1].title;
        if (user.email === owner) {
          let aboveDialog = $("#modal-dialog-above")
            .html("<div style='font-size: 90%;'>Are you sure you want to delete <b>" + this.fileName + "</b>?</div>");
          aboveDialog.dialog({
            resizable: false,
            modal: true,
            title: "Delete File",
            height: 200,
            width: 350,
            buttons: {
              'OK': () => {
                this.firestore.collection("users").doc(user.email).collection("files").doc(this.fileName).delete().then(() => {
                  cloudFiles.splice(this.selectedIndex - 1, 1); // minus 1 as the header takes up the 0 position
                  table.deleteRow(this.selectedIndex);
                });
                aboveDialog.dialog('close');
                this.setCloudFileTitle('');
              },
              'Cancel': () => aboveDialog.dialog('close')
            }
          });
        } else {
          let aboveDialog = $("#modal-dialog-above")
            .html("<div style='font-size: 90%;'>Are you sure you don't want the shared file <b>" + this.fileName + "</b>?</div>");
          aboveDialog.dialog({
            resizable: false,
            modal: true,
            title: "Remove Shared File",
            height: 200,
            width: 350,
            buttons: {
              'OK': () => {
                let docRef = this.firestore.collection("users").doc(user.email).collection("shared").doc(owner);
                docRef.get().then((doc) => {
                  cloudFiles.splice(this.selectedIndex - 1, 1); // minus 1 as the header takes up the 0 position
                  table.deleteRow(this.selectedIndex);
                  let arr = doc.data()["files"];
                  arr.removeItem(this.fileName);
                  docRef.set({files: arr});
                });
                aboveDialog.dialog('close');
                this.setCloudFileTitle('');
              },
              'Cancel': () => aboveDialog.dialog('close')
            }
          });
        }
      } else {
        this.pleaseSelectFile();
      }
    };

    // rename function
    let rename = () => {
      if (this.selectedIndex > 0) {
        const owner = table.rows[this.selectedIndex].cells[1].title;
        if (user.email === owner) {
          const oldName: string = table.rows[this.selectedIndex].cells[0].innerHTML;
          let newName: string;
          let aboveDialog = $("#modal-dialog-above").html("<div><input type='text' id='rename-file-field' style='width: 100%'></div>");
          let nameField = document.getElementById('rename-file-field') as HTMLInputElement;
          nameField.value = oldName;
          const okFunction = () => {
            newName = (<HTMLInputElement>document.getElementById('rename-file-field')).value;
            if (newName !== oldName && newName.trim() !== '') {
              const files = this.firestore.collection("users").doc(user.email).collection("files");
              files.doc(oldName).get().then(doc => {
                if (doc && doc.exists) {
                  files.doc(newName).set(doc.data()).then(() => files.doc(oldName).delete());
                }
              });
              aboveDialog.dialog('close');
              if (document.getElementById("main-page-cloud-file-title-label").innerHTML === oldName) this.setCloudFileTitle(newName);
              table.rows[this.selectedIndex].cells[0].innerHTML = newName;
            }
          };
          nameField.addEventListener("keyup", (e) => {
            if (e.key == "Enter") {
              okFunction();
            }
          });
          aboveDialog.dialog({
            resizable: false,
            modal: true,
            title: "Rename File",
            height: 160,
            width: 350,
            buttons: {
              'OK': okFunction,
              'Cancel': () => aboveDialog.dialog('close')
            }
          });
        } else {
          let aboveDialog = $("#modal-dialog-above")
            .html("<div style='font-size: 90%;'>You are not allowed to rename a shared file.</div>");
          aboveDialog.dialog({
            resizable: false,
            modal: true,
            title: "Renaming not permitted",
            height: 200,
            width: 350,
            buttons: {
              'OK': () => aboveDialog.dialog('close')
            }
          });
        }
      } else {
        this.pleaseSelectFile();
      }
    };

    // show dialog
    d.dialog({
      resizable: true,
      modal: true,
      title: "Your Files in the Cloud",
      height: 500,
      width: 750,
      buttons: {
        'Open': open,
        'Rename': rename,
        'Delete': deletion,
        'Cancel': () => d.dialog('close')
      }
    });
  }

  shareFileWithEmail(email: string): void {
    this.firestore.collection("users").get().then(querySnapshot => {
      let found = false;
      for (let doc of querySnapshot.docs) {
        if (doc.id === email) {
          found = true;
          break;
        }
      }
      if (found) {
        let docRef = this.firestore.collection("users").doc(email).collection("shared").doc(user.email);
        docRef.get().then(doc => {
          let data = doc.data();
          if (data) {
            let arr = data["files"];
            if (!arr.includes(this.fileName)) { // not already shared
              arr.push(this.fileName);
              docRef.set({files: arr});
              emailClient.send(email, "Someone just shared a file in " + Software.name + " with you",
                "Hello,<p>" + user.email + " shared a file <b>" + this.fileName + "</b> with you.</p>"
                + "<p>Please check it out in <a href='" + Software.url + "'>your " + Software.name + " account</a>.</p>"
                + "<br><p>Sincerely yours,<br>The Institute for Future Intelligence<br><a href='https://intofuture.org'>intofuture.org</a></p>");
            }
          } else {
            let arr: string[] = [this.fileName];
            docRef.set({files: arr});
          }
        });
      } else {
        let d = $("#modal-dialog-above");
        d.html("<div>Sorry, you cannot share with <b>" + email + "</b>,<br>as it is not registered with " + Software.name + ".</div>").dialog({
          resizable: false,
          modal: true,
          title: "User not recognized",
          height: 220,
          width: 400,
          buttons: {
            'OK': () => d.dialog('close')
          }
        });
      }
    });
  }

  shareFileInCloud(): void {
    if (this.isCloudFile()) {
      let d = $("#modal-dialog").html("<div><input type='text' id='share-email-field' style='width: 100%'></div>");
      let emailField = document.getElementById('share-email-field') as HTMLInputElement;
      const okFunction = () => {
        const emails = (<HTMLInputElement>document.getElementById('share-email-field')).value;
        let emailsArray = emails.split(/[ ,]+/);
        for (let email of emailsArray) {
          this.shareFileWithEmail(email);
        }
        d.dialog('close');
      };
      emailField.addEventListener("keyup", (e) => {
        if (e.key == "Enter") {
          okFunction();
        }
      });
      d.dialog({
        resizable: false,
        modal: true,
        title: "Email of whom this is to be shared",
        height: 160,
        width: 350,
        buttons: {
          'OK': okFunction,
          'Cancel': () => d.dialog('close')
        }
      });
    } else {
      let d = $("#modal-dialog");
      d.html("<div>This file is not in the cloud. Save it to the cloud<br>first and then share it.</div>").dialog({
        resizable: false,
        modal: true,
        title: "Not a Cloud File",
        height: 220,
        width: 360,
        buttons: {
          'OK': () => d.dialog('close')
        }
      });
    }
  }

  setCloudFileTitle(title: string): void {
    const titleLabel = document.getElementById("main-page-cloud-file-title-label");
    titleLabel.innerHTML = title;
    titleLabel.style.display = 'inline';
    const cloudIndicator = document.getElementById("main-page-cloud-indicator");
    cloudIndicator.style.display = (title && title.trim() !== '') ? 'inline' : 'none';
  }

  isCloudFile(): boolean {
    const title = document.getElementById("main-page-cloud-file-title-label").innerHTML;
    return title && title.trim() !== '';
  }

  private pleaseSelectFile(): void {
    let aboveDialog = $("#modal-dialog-above").html("<div style='font-size: 90%;'>Please select a file.</div>");
    aboveDialog.dialog({
      resizable: false,
      modal: true,
      title: "No Selected File",
      height: 150,
      width: 350,
      buttons: {
        'OK': () => aboveDialog.dialog('close')
      }
    });
  }

}
